import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, ArrowRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useAnnouncement } from "../lib/announcement";

const pick = (f, lang) => (f && (f[lang] || f.fr)) || "";

export const AnnouncementBar = () => {
  const { lang } = useLang();
  const { data, active, modalOpen, openModal, closeModal } = useAnnouncement();

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [modalOpen]);

  if (!active) return null;

  const banner = pick(data.banner_text, lang);
  const title = pick(data.modal_title, lang);
  const body = pick(data.modal_body, lang);
  const ctaLabel = pick(data.cta_label, lang);
  const ctaUrl = data.cta_url || "";
  const isExternal = /^https?:\/\//i.test(ctaUrl);

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="announcement-banner"
        className="fixed top-0 left-0 right-0 z-[120] h-9 w-full flex items-center justify-center gap-2 px-4 bg-ink border-b border-accent-yellow/25 text-white/85 hover:text-white transition-colors duration-300 group"
      >
        <Plus size={12} strokeWidth={3} className="text-accent-yellow shrink-0" />
        <span className="text-[11px] md:text-xs tracking-wide truncate max-w-[78vw] font-light">
          {banner}
        </span>
        <ArrowRight
          size={12}
          className="hidden sm:block text-accent-yellow/70 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
        />
      </button>

      <AnimatePresence>
        {modalOpen && (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-5"
            data-testid="announcement-modal"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeModal}
              className="absolute inset-0 bg-ink/80 backdrop-blur-md"
              data-testid="announcement-modal-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-lg rounded-3xl border border-white/12 bg-ink shadow-2xl shadow-black/60 p-8 md:p-10"
            >
              <button
                onClick={closeModal}
                data-testid="announcement-modal-close"
                aria-label="Fermer"
                className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors duration-200"
              >
                <X size={20} />
              </button>
              <div className="h-px w-10 bg-accent-yellow mb-6" />
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-tight">
                {title}
              </h2>
              {body && (
                <p className="mt-4 text-sm md:text-base text-white/60 font-light leading-relaxed whitespace-pre-line">
                  {body}
                </p>
              )}
              {ctaLabel && ctaUrl && (
                isExternal ? (
                  <a
                    href={ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="announcement-modal-cta"
                    className="group mt-8 inline-flex items-center gap-3 px-7 py-3.5 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors duration-300"
                  >
                    {ctaLabel}
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                ) : (
                  <Link
                    to={ctaUrl}
                    onClick={closeModal}
                    data-testid="announcement-modal-cta"
                    className="group mt-8 inline-flex items-center gap-3 px-7 py-3.5 rounded-xl bg-accent-yellow text-ink text-sm font-semibold hover:bg-white transition-colors duration-300"
                  >
                    {ctaLabel}
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
